import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import { GoogleLogo, EnvelopeSimple, Lock, User, ChartLineUp, LinkSimple, Gauge, Spinner } from '@phosphor-icons/react';

const C = {
  bg:      '#0a0a0f',
  surface: '#13131f',
  surfaceHi:'#181828',
  border:  'rgba(124,58,237,0.2)',
  borderHi:'rgba(124,58,237,0.45)',
  violet:  '#7c3aed',
  blue:    '#3b82f6',
  text:    '#f1f5f9',
  muted:   '#94a3b8',
  faint:   '#475569',
  gradient:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  font:    "'Inter', system-ui, -apple-system, sans-serif",
};

const PERKS = [
  { icon: ChartLineUp, label: 'Daily rank tracking',   color: '#7c3aed' },
  { icon: LinkSimple,  label: 'Backlink monitoring',   color: '#3b82f6' },
  { icon: Gauge,       label: 'Full site SEO audit',   color: '#10b981' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register, initiateOAuthLogin } = useAuthStore();
  const [isLogin, setIsLogin]   = useState(true);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        try {
          const st = await apiClient.get('/auth/onboarding-status');
          navigate(st.should_onboard ? '/onboarding' : '/dashboard');
        } catch { navigate('/dashboard'); }
      } else {
        await register(email, password, name);
        navigate('/onboarding');
      }
    } catch (err) { setError(err.message || 'Authentication failed'); }
    finally { setLoading(false); }
  };

  const inp = (extra = {}) => ({
    width: '100%',
    padding: '11px 12px 11px 40px',
    background: '#1a1a2e',
    border: `1px solid rgba(124,58,237,0.3)`,
    borderRadius: 10,
    fontSize: 14,
    color: C.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: C.font,
    transition: 'border-color 0.2s',
    ...extra,
  });

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>

      {/* Glow */}
      <div style={{ position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: 420, position: 'relative' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
            <div style={{ width: 36, height: 36, background: C.gradient, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, boxShadow: '0 2px 16px rgba(124,58,237,0.4)' }}>✦</div>
            <span style={{ fontWeight: 800, fontSize: 18, color: C.text, letterSpacing: '-0.5px' }}>
              SEO Dash <span style={{ color: C.violet }}>360</span>
            </span>
          </Link>
        </div>

        {/* Card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '32px 32px 28px', boxShadow: '0 8px 40px rgba(124,58,237,0.12)' }}>

          <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: '0 0 4px', letterSpacing: '-0.5px', textAlign: 'center' }}>
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>
          <p style={{ fontSize: 13, color: C.muted, margin: '0 0 24px', textAlign: 'center' }}>
            {isLogin ? 'Sign in to access your dashboard' : 'Start your free SEO analysis today'}
          </p>

          {/* Google */}
          <button onClick={initiateOAuthLogin}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px 0', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 13, fontWeight: 600, color: C.text, cursor: 'pointer', marginBottom: 20, fontFamily: C.font, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}>
            <GoogleLogo weight="bold" style={{ width: 16, height: 16, color: '#ea4335' }} />
            Continue with Google
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
            <span style={{ fontSize: 11, color: C.faint, fontWeight: 500 }}>or continue with email</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</label>
                <div style={{ position: 'relative' }}>
                  <User style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: C.faint }} />
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required={!isLogin}
                    style={inp()}
                    onFocus={e => e.target.style.borderColor = C.violet}
                    onBlur={e => e.target.style.borderColor = 'rgba(124,58,237,0.3)'} />
                </div>
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
              <div style={{ position: 'relative' }}>
                <EnvelopeSimple style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: C.faint }} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required
                  style={inp()}
                  onFocus={e => e.target.style.borderColor = C.violet}
                  onBlur={e => e.target.style.borderColor = 'rgba(124,58,237,0.3)'} />
              </div>
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: C.faint }} />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={6}
                  style={inp()}
                  onFocus={e => e.target.style.borderColor = C.violet}
                  onBlur={e => e.target.style.borderColor = 'rgba(124,58,237,0.3)'} />
              </div>
            </div>

            {error && (
              <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: '#f87171', marginBottom: 18 }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px 0', background: loading ? C.faint : C.gradient, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: C.font, boxShadow: loading ? 'none' : '0 4px 16px rgba(124,58,237,0.3)', transition: 'opacity 0.15s' }}>
              {loading ? <Spinner style={{ width: 14, height: 14 }} className="animate-spin" /> : null}
              {loading ? 'Please wait…' : isLogin ? 'Sign in' : 'Create account'}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: 13, color: C.muted, margin: 0 }}>
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
              style={{ marginLeft: 6, fontSize: 13, fontWeight: 700, color: C.violet, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: C.font }}>
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>

        {/* Perks */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 28 }}>
          {PERKS.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <p.icon style={{ width: 13, height: 13, color: p.color }} />
              <span style={{ fontSize: 11, color: C.faint }}>{p.label}</span>
            </div>
          ))}
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: C.faint, margin: '16px 0 0' }}>
          By continuing you agree to our{' '}
          <a href="#" style={{ color: C.faint, textDecoration: 'underline' }}>Terms</a> &{' '}
          <a href="#" style={{ color: C.faint, textDecoration: 'underline' }}>Privacy</a>
        </p>
      </div>
    </div>
  );
}
